import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Star,
} from 'lucide-angular';
import { AuthService } from '../auth.service';
import { UserType } from '../auth.models';
import { AppEventsService } from '../../analytics/app-events.service';
import { inferAnalyticsSource } from '../../analytics/infer-analytics-source';
import { TrackingCookieService } from '../../analytics/tracking-cookie.service';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink],
  templateUrl: './sign-up.html',
  styleUrl: './sign-up.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly appEvents = inject(AppEventsService);
  private readonly trackingCookieService = inject(TrackingCookieService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly errorMessage$ = new BehaviorSubject<string>('');
  protected readonly isUserExistsError$ = new BehaviorSubject<boolean>(false);
  protected readonly showPassword = signal(false);

  protected readonly authForm: FormGroup;

  protected readonly icons = {
    ArrowRight,
    ArrowLeft,
    Eye,
    EyeOff,
    Mail,
    Lock,
    Star,
  };

  /** User type from query params */
  protected readonly userType: UserType;

  /** Redirect URL from query params (set by the caller, feature-agnostic) */
  protected readonly redirectUrl: string | null;

  /** Whether we're in a funnel mode (has a redirectUrl pointing to a feature) */
  protected readonly isFunnelMode: boolean;

  /** Query params to forward when navigating between sign-up and login */
  protected readonly forwardQueryParams: Record<string, string>;

  constructor() {
    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });

    const params = this.route.snapshot.queryParams;
    this.redirectUrl = params['redirectUrl'] || null;
    this.isFunnelMode = !!this.redirectUrl;
    this.userType = (params['userType'] as UserType) || 'teacher';

    // Build query params to forward when switching between sign-up ↔ login
    const forward: Record<string, string> = {};
    if (this.redirectUrl) {
      forward['redirectUrl'] = this.redirectUrl;
    }
    if (this.userType !== 'teacher') {
      forward['userType'] = this.userType;
    }
    this.forwardQueryParams = forward;
  }

  ngOnInit(): void {
    // Scroll to top when component initializes to fix mobile scroll position issue
    // This ensures the sign-up form is visible immediately instead of being scrolled out of view
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected goBack(): void {
    if (this.redirectUrl) {
      // Navigate back to the feature page (path only, without action query params)
      const path = this.redirectUrl.split('?')[0];
      this.router.navigateByUrl(path);
    } else {
      this.router.navigate(['/']);
    }
  }

  protected onSubmit(): void {
    if (this.authForm.invalid) {
      this.authForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    this.errorMessage$.next('');

    const { email, password } = this.authForm.value;
    const trackingData = this.trackingCookieService.getTrackingData();
    const request = { email, password, userType: this.userType, trackingData };

    this.authService
      .signUp(request, this.redirectUrl ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading$.next(false);

          // Infer analytics source from redirectUrl
          const source = inferAnalyticsSource(this.redirectUrl);
          this.appEvents.signUpCompleted(source, response.eventId);

          this.handleAuthSuccess();
        },
        error: (error) => {
          this.isLoading$.next(false);
          const isConflict = error.status === 409;
          this.isUserExistsError$.next(isConflict);
          if (isConflict) {
            this.errorMessage$.next('An account with this email already exists.');
          } else {
            this.errorMessage$.next(error.error?.message || 'An error occurred. Please try again.');
          }
        },
      });
  }

  protected signInWithGoogle(): void {
    this.authService.signInWithGoogle(this.redirectUrl ?? undefined, this.userType);
  }

  protected signInWithMicrosoft(): void {
    this.authService.signInWithMicrosoft(this.redirectUrl ?? undefined, this.userType);
  }

  private handleAuthSuccess(): void {
    // If a redirectUrl was provided, go there; otherwise let RoleRedirectComponent handle it
    this.router.navigateByUrl(this.redirectUrl ?? '/dashboard');
  }
}

